import groupBy from 'lodash/groupBy';
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useApi } from '@/lib/api';
import { useUser } from './user';
import { useGlobalStore } from '@/store/global-store-base';
import { UnitDisplayFormat, type PhysicalQuantity, type UnitIdAndCount, type UnitDto } from '@/ApiClient';
import { formatValue as formatDyadicValue } from '@lib/dyadic-fractions';

export const useUnits = defineStore('units', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.unit_ListUnits,
    dto: 'UnitDto',
    create: api.unit_CreateUnit,
    update: api.unit_UpdateUnit,
    delete: api.unit_DeleteUnit,
    signalR: 'UnitUpdate',
  });

  const { loaded, getByIds, getById } = globalStoreBase;

  const mostUsed = ref<UnitIdAndCount[] | null>(null);

  const forComponents = computed(() => loaded.value.filter((unit) => unit.canUseForComponentUnit));
  const forProducts = computed(() => loaded.value.filter((unit) => unit.canUseForProductUnit));
  const byPhysicalQuantity = computed(() => {
    return groupBy(loaded.value, 'physicalQuantity');
  });
  // @ts-ignore PhysicalQuantity is string, but more specific
  const byPhysicalQuantityForComponents = computed<Record<PhysicalQuantity, UnitDto[]>>(() => {
    return groupBy(forComponents.value, 'physicalQuantity');
  });
  const physicalQuantities = computed(() => {
    return Object.keys(groupBy(loaded.value, 'physicalQuantity')).sort();
  });
  const physicalQuantitiesForComponents = computed(() => {
    return Object.keys(groupBy(forComponents.value, 'physicalQuantity')).sort();
  });

  function getTopUnits() {
    const mostUsedUnitIds = mostUsed.value!.map((c) => c.id);

    return getByIds(mostUsedUnitIds).slice(0, 5);
  }

  async function getMostUsed() {
    mostUsed.value = await api.unit_GetMostUsedUnits(useUser().brandId);
  }

  function getPiecesUnit() {
    return loaded.value.find((unit) => unit.symbol === 'pcs');
  }

  function getComponentDefaultUnit() {
    return getPiecesUnit() || loaded.value.find((unit) => unit.canUseForComponentUnit);
  }

  function getDisplayFormat(unitId: number): UnitDisplayFormat {
    return getById(unitId)?.displayFormat ?? UnitDisplayFormat.Decimal;
  }

  function numberWithSymbol(value: number | undefined | null, unitId: number) {
    if (value === null || value === undefined) return null;

    const unit = getById(unitId);

    if (!unit) throw new Error('No unit found!');

    const formatted = formatDyadicValue(value, unit.displayFormat) ?? value.toString();
    return `${formatted} ${unit.symbol}`;
  }

  return {
    ...globalStoreBase,
    mostUsed,
    forComponents,
    forProducts,
    byPhysicalQuantity,
    byPhysicalQuantityForComponents,
    physicalQuantities,
    physicalQuantitiesForComponents,
    getTopUnits,
    getMostUsed,
    getPiecesUnit,
    getComponentDefaultUnit,
    numberWithSymbol,
    getDisplayFormat,
  };
});
